import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../context/AuthContext';
import type {Employee, NewEmployee} from '../types/employee';
import type {Bank, BankAccount, BankAccountTextField} from '../types/contract';
import {getBanks} from '../services/bankService';
import type {Contract} from '../types/contract';
import {emptyBankAccount} from '../utils/bankAccounts';
import {API_BASE_URL} from '../config/apiConfig';
import i18n from '../i18n';
import {
    activateEmployees,
    addContract,
    addEmployee,
    deactivateEmployees,
    deleteEmployees,
    getContracts,
    getEmployees,
    searchEmployees,
    terminateContract,
    updateEmployee
} from '../services/employeeService';

export interface UploadedEmployee {
    _rowId: number;
    branch: string;
    firstName: string;
    lastName: string;
    personalNo: string;
    email: string;
    phone: string;
    employmentType: string;
    taxId?: string;
    status: boolean;
    isCitizen: boolean;
    isTaxEmpt: boolean;
    residencyStatus: string;
    bankAccount?: { bankId?: string | null; accountNumber?: string | null } | null;
}

export interface UploadRowError {
    row?: number;
    column?: string;
    message: string;
}

export interface UploadError {
    message: string;
    errors: UploadRowError[];
}

export interface ApprovedBranch {
    id: string;
    name: string;
}

export const EMPLOYMENT_TYPE_OPTIONS = ['Full Time', 'Part Time', 'Contractor', 'Temporary'] as const;

export const RESIDENCY_STATUS_OPTIONS = [
    'Permanent Residency',
    'Temporary Residency',
    'Already Pensioner',
] as const;

export interface FormValidationErrors {
    firstName?: string;
    lastName?: string;
    personalNo?: string;
    email?: string;
    phone?: string;
    employmentType?: string;
    taxId?: string;
    residencyStatus?: string;
    // Only checked when the form carries a bank account (the add form).
    bankAccount?: { bankId?: string; accountNumber?: string };
}

export function validateEmployeeForm(employee: NewEmployee): FormValidationErrors {
    const errors: FormValidationErrors = {};
    if (!(employee.firstName || '').trim()) errors.firstName = i18n.t('employee.upload.validation.firstNameRequired');
    if (!(employee.lastName || '').trim()) errors.lastName = i18n.t('employee.upload.validation.lastNameRequired');
    if (!(employee.personalNo || '').trim()) errors.personalNo = i18n.t('employee.upload.validation.personalNoRequired');
    if (!(employee.email || '').trim()) {
        errors.email = i18n.t('employee.upload.validation.emailRequired');
    } else if (!EMAIL_REGEX.test(employee.email)) {
        errors.email = i18n.t('employee.upload.validation.emailInvalid');
    }
    if (!(employee.phone || '').trim()) {
        errors.phone = i18n.t('employee.upload.validation.phoneRequired');
    } else if (!PHONE_REGEX.test(employee.phone)) {
        errors.phone = i18n.t('employee.upload.validation.phoneFormat');
    }
    if (!(employee.employmentType || '').trim()) errors.employmentType = i18n.t('employee.upload.validation.employmentTypeRequired');
    if (!(employee.residencyStatus || '').trim()) errors.residencyStatus = i18n.t('employee.upload.validation.residencyStatusRequired');
    if (employee.bankAccount) {
        const accountErrors: { bankId?: string; accountNumber?: string } = {};
        if (!(employee.bankAccount.bankId || '').trim()) accountErrors.bankId = i18n.t('employee.upload.validation.bankRequired');
        if (!(employee.bankAccount.accountNumber || '').trim()) accountErrors.accountNumber = i18n.t('employee.upload.validation.accountNumberRequired');
        if (accountErrors.bankId || accountErrors.accountNumber) errors.bankAccount = accountErrors;
    }
    return errors;
}

export function isFormValid(employee: NewEmployee): boolean {
    return Object.keys(validateEmployeeForm(employee)).length === 0;
}

export interface NewEmployeeFormComponentProps {
    newEmployee: NewEmployee;
    handleFormChange: (field: keyof NewEmployee) => (event: any) => void;
    handleCheckboxChange: (field: keyof NewEmployee) => (event: any) => void;
    showAllErrors?: boolean;
    showBranch?: boolean;
    backendError?: { message: string; violations: string[] } | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{6,12}$/;

export const useEmployeeHandlers = () => {
    const {token} = useAuth();
    const {t} = useTranslation();
    const [employees, setEmployees] = React.useState<Employee[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);
    const [contractSubmitError, setContractSubmitError] = React.useState<{ message: string; violations: string[] } | null>(null);
    const [addEmployeeSubmitError, setAddEmployeeSubmitError] = React.useState<{ message: string; violations: string[] } | null>(null);
    const [editEmployeeSubmitError, setEditEmployeeSubmitError] = React.useState<{ message: string; violations: string[] } | null>(null);
    const [selected, setSelected] = React.useState<number[]>([]);
    const [searchInput, setSearchInput] = React.useState<string>('');
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [branchFilter, setBranchFilter] = React.useState<string>('');
    const [statusFilter, setStatusFilter] = React.useState<string>('');
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [itemsPerPage] = React.useState<number>(10);
    const [openDialog, setOpenDialog] = React.useState<boolean>(false);
    const [openEditDialog, setOpenEditDialog] = React.useState<boolean>(false);
    const [openViewDialog, setOpenViewDialog] = React.useState<boolean>(false);
    const [openAddContractDialog, setOpenAddContractDialog] = React.useState<boolean>(false);
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false);
    const [confirmDialogMessage, setConfirmDialogMessage] = React.useState<string>('');
    const [confirmDialogAction, setConfirmDialogAction] = React.useState<(() => void) | null>(null);
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
    const [successMessageText, setSuccessMessageText] = React.useState<string>(t('common.operationCompleted'));
    const [selectedEmployeeContract, setSelectedEmployeeContract] = React.useState<Contract | null>(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number | null>(null);
    const [newEmployee, setNewEmployee] = React.useState<NewEmployee>({
        firstName: '',
        lastName: '',
        personalNo: '',
        email: '',
        phone: '',
        hireDate: '',
        employmentType: 'Full Time',
        status: 'Active',
        isCitizen: true,
        taxExempt: false,
        residencyStatus: 'Permanent Residency'
        ,
        branch: '',
        bankAccount: emptyBankAccount()
    });
    const [editingEmployee, setEditingEmployee] = React.useState<NewEmployee>({
        firstName: '',
        lastName: '',
        personalNo: '',
        email: '',
        phone: '',
        hireDate: '',
        employmentType: 'Full Time',
        status: 'Active',
        taxId: '',
        isCitizen: true,
        taxExempt: false,
        residencyStatus: ''
        ,
        branch: ''
    });
    const [editingEmployeeId, setEditingEmployeeId] = React.useState<number | null>(null);
    const [newContract, setNewContract] = React.useState<Contract>({
        contractNumber: '',
        contractId: '',
        startDate: '',
        endDate: '',
        status: 'active',
        jobPosition: '',
        payrollCalcType: '',
        withContribution: '',
        salaryBasis: '',
        baseAmount: '',
        workload: '',
        employerInsuranceBenefit: '',
        familyInsuranceDeduction: '',
        fundEnabled: '',
        notes: ''
    });

    // Contract pagination state
    const [contracts, setContracts] = React.useState<Contract[]>([]);
    const [contractsPage, setContractsPage] = React.useState<number>(0);
    const [contractsTotalRecords, setContractsTotalRecords] = React.useState<number>(0);

    const branchOptions = React.useMemo(() => {
        const set = new Set<string>();
        employees.forEach((e) => {
            if (e.branch) set.add(e.branch);
        });
        return Array.from(set).sort();
    }, [employees]);

    const statusOptions = React.useMemo(() => {
        const set = new Set<string>();
        employees.forEach((e) => {
            if (e.status) set.add(e.status);
        });
        return Array.from(set).sort();
    }, [employees]);

    const facetFilteredEmployees = React.useMemo(() => {
        return employees.filter((e) =>
            (!branchFilter || e.branch === branchFilter) &&
            (!statusFilter || e.status === statusFilter)
        );
    }, [employees, branchFilter, statusFilter]);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [branchFilter, statusFilter]);

    const showConfirmDialog = (message: string, onConfirm: () => void) => {
        setConfirmDialogMessage(message);
        setConfirmDialogAction(() => onConfirm);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDialogClose = () => {
        setOpenConfirmDialog(false);
        // Use setTimeout to ensure cleanup happens after dialog closes
        setTimeout(() => {
            setConfirmDialogMessage('');
            setConfirmDialogAction(null);
        }, 100);
    };

    const handleConfirmDialogNo = () => {
        handleConfirmDialogClose();
    };

    const handleConfirmDialogYes = () => {
        if (confirmDialogAction) {
            confirmDialogAction();
        }
        handleConfirmDialogClose();
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.checked) {
            const newSelected = facetFilteredEmployees.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<HTMLTableRowElement>, id: number): void => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const isSelected = (id: number): boolean => selected.includes(id);

    const handleEdit = (id: number): void => {
        console.log('Edit employee:', id);

        // Find the employee by id
        const employeeToEdit = employees.find(emp => emp.id === id);

        if (employeeToEdit) {
            // Populate the editing form with employee data
            // Spread known properties and fallback to possible alternative keys for personal number
            const anyEmp: any = employeeToEdit as any;
            setEditingEmployee({
                firstName: employeeToEdit.firstName ?? '',
                lastName: employeeToEdit.lastName ?? '',
                personalNo: employeeToEdit.personalNo ?? anyEmp.personalNumber ?? anyEmp.personal_no ?? '',
                email: employeeToEdit.email ?? '',
                phone: employeeToEdit.phone ?? '',
                hireDate: employeeToEdit.hireDate ?? '',
                employmentType: employeeToEdit.employmentType ?? 'Full Time',
                status: employeeToEdit.status ?? 'Active',
                taxId: employeeToEdit.taxId ?? '',
                // These fields are not in Employee type but needed for the form (use existing if provided)
                isCitizen: (anyEmp.isCitizen !== undefined) ? anyEmp.isCitizen : true,
                taxExempt: (anyEmp.taxExempt !== undefined) ? anyEmp.taxExempt : false,
                residencyStatus: anyEmp.residencyStatus ?? 'Permanent Residency',
                branch: employeeToEdit.branch ?? ''
            });
            setEditingEmployeeId(id);
            setOpenEditDialog(true);
        }
    };

    const handleDelete = (id: number): void => {
        console.log('Delete employee:', id);

        if (!token) {
            console.error('No token found');
            setError(t('employee.upload.error.noAuthToken'));
            return;
        }

        // Show confirmation dialog
        showConfirmDialog(
            t('employee.confirm.deleteEmployee'),
            async () => {
                setLoading(true);

                try {
                    const result = await deleteEmployees(token, [id.toString()]);
                    console.log('Delete result:', result);

                    if (result.success) {
                        // Refresh the employees list
                        const employeesResult = await getEmployees(token);
                        if (employeesResult.success && employeesResult.employees) {
                            setEmployees(employeesResult.employees);
                        }
                        setError(null);
                        // Remove from selected if it was selected
                        setSelected(selected.filter(selectedId => selectedId !== id));
                    } else {
                        setError(result.error || t('employee.error.deleteFailed'));
                    }
                } catch (err) {
                    console.error('Error in handleDelete:', err);
                    setError(t('common.error.unexpected'));
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleView = async (id: number): Promise<void> => {
        console.log('View employee contracts:', id);

        if (!token) {
            console.error('No token found');
            setError(t('employee.upload.error.noAuthToken'));
            return;
        }

        setLoading(true);

        try {
            // Fetch contracts for the employee with current pagination settings (0-based, 1 per page)
            const result = await getContracts(token, id.toString(), 1, contractsPage);
            console.log('getContracts result:', result);

            if (result.success && result.contracts) {
                setContracts(result.contracts);
                setContractsTotalRecords(result.totalRecords || 0);
                setSelectedEmployeeId(id);

                // Set the first contract as selected for viewing (if any)
                if (result.contracts.length > 0) {
                    setSelectedEmployeeContract(result.contracts[0]);
                } else {
                    setSelectedEmployeeContract(null);
                }

                setOpenViewDialog(true);
                setError(null);
            } else {
                setError(result.error || t('employee.error.fetchEmployeeContractsFailed'));
            }
        } catch (err) {
            console.error('Error in handleView:', err);
            setError(t('common.error.unexpected'));
        } finally {
            setLoading(false);
        }
    };

    const handleCloseViewDialog = (): void => {
        setOpenViewDialog(false);
        setSelectedEmployeeContract(null);
        setSelectedEmployeeId(null);
        setContracts([]);
        setContractsTotalRecords(0);
        // Reset pagination to initial state (0-based)
        setContractsPage(0);
    };

    // Contract pagination handlers
    const handleContractsPageChange = async (newPage: number): Promise<void> => {
        if (!token || !selectedEmployeeId) {
            return;
        }

        setLoading(true);
        try {
            const result = await getContracts(token, selectedEmployeeId.toString(), 1, newPage);
            if (result.success && result.contracts) {
                setContracts(result.contracts);
                setContractsTotalRecords(result.totalRecords || 0);
                setContractsPage(newPage);

                // Update selected contract to first one of the new page
                if (result.contracts.length > 0) {
                    setSelectedEmployeeContract(result.contracts[0]);
                } else {
                    setSelectedEmployeeContract(null);
                }
                setError(null);
            } else {
                setError(result.error || t('employee.error.fetchContractsFailed'));
            }
        } catch (err) {
            console.error('Error in handleContractsPageChange:', err);
            setError(t('common.error.unexpected'));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmTerminate = async (
        userId: string | number,
        contractId: string,
        onContractsPageChange: (page: number) => Promise<void>,
        contractsPage: number,
        setConfirmDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
        setLoading: React.Dispatch<React.SetStateAction<boolean>>,
        setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
        setSuccessMessage: React.Dispatch<React.SetStateAction<string>>
    ): Promise<void> => {
        setConfirmDialogOpen(false);
        setLoading(true);
        setErrorMessage('');

        try {
            if (!token) {
                throw new Error(t('employee.upload.error.noAuthToken'));
            }
            const result = await terminateContract(token, userId, contractId);
            if (result.success) {
                setSuccessMessage(t('employee.success.contractTerminated'));
                // Refresh the contracts list to show the updated status
                await onContractsPageChange(contractsPage);

                // Also refresh the employees list to update the TableComponent
                const employeesResult = await getEmployees(token);
                if (employeesResult.success && employeesResult.employees) {
                    setEmployees(employeesResult.employees);
                }
            } else {
                throw new Error(result.error || t('employee.error.terminateContractFailed'));
            }
        } catch (error) {
            console.error('Error terminating contract:', error);
            setErrorMessage(error instanceof Error ? error.message : t('employee.error.terminateContractGeneric'));
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = (): void => {
        console.log('Activate selected employees:', selected);

        if (!token) {
            console.error('No token found');
            setError(t('employee.upload.error.noAuthToken'));
            return;
        }

        if (selected.length === 0) {
            setError(t('employee.error.noEmployeesSelected'));
            return;
        }

        // Show confirmation dialog
        showConfirmDialog(
            t('employee.confirm.activate', {count: selected.length}),
            async () => {
                setLoading(true);

                try {
                    const result = await activateEmployees(token, selected.map(id => id.toString()));
                    console.log('Activate result:', result);

                    if (result.success) {
                        // Refresh the employees list
                        const employeesResult = await getEmployees(token);
                        if (employeesResult.success && employeesResult.employees) {
                            setEmployees(employeesResult.employees);
                        }
                        setError(null);
                        // Clear selection
                        setSelected([]);
                    } else {
                        setError(result.error || t('employee.error.activateFailed'));
                    }
                } catch (err) {
                    console.error('Error in handleActivate:', err);
                    setError(t('common.error.unexpected'));
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleDeactivate = (): void => {
        console.log('Deactivate selected employees:', selected);

        if (!token) {
            console.error('No token found');
            setError(t('employee.upload.error.noAuthToken'));
            return;
        }

        if (selected.length === 0) {
            setError(t('employee.error.noEmployeesSelected'));
            return;
        }

        // Show confirmation dialog
        showConfirmDialog(
            t('employee.confirm.deactivate', {count: selected.length}),
            async () => {
                setLoading(true);

                try {
                    const result = await deactivateEmployees(token, selected.map(id => id.toString()));
                    console.log('Deactivate result:', result);

                    if (result.success) {
                        // Refresh the employees list
                        const employeesResult = await getEmployees(token);
                        if (employeesResult.success && employeesResult.employees) {
                            setEmployees(employeesResult.employees);
                        }
                        setError(null);
                        // Clear selection
                        setSelected([]);
                    } else {
                        setError(result.error || t('employee.error.deactivateFailed'));
                    }
                } catch (err) {
                    console.error('Error in handleDeactivate:', err);
                    setError(t('common.error.unexpected'));
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleBulkDelete = (): void => {
        console.log('Bulk delete selected employees:', selected);

        if (!token) {
            console.error('No token found');
            setError(t('employee.upload.error.noAuthToken'));
            return;
        }

        if (selected.length === 0) {
            setError(t('employee.error.noEmployeesSelected'));
            return;
        }

        // Show confirmation dialog
        showConfirmDialog(
            t('employee.confirm.bulkDelete', {count: selected.length}),
            async () => {
                setLoading(true);

                try {
                    const result = await deleteEmployees(token, selected.map(id => id.toString()));
                    console.log('Bulk delete result:', result);

                    if (result.success) {
                        // Refresh the employees list
                        const employeesResult = await getEmployees(token);
                        if (employeesResult.success && employeesResult.employees) {
                            setEmployees(employeesResult.employees);
                        }
                        setError(null);
                        // Clear selection
                        setSelected([]);
                    } else {
                        setError(result.error || t('employee.error.bulkDeleteFailed'));
                    }
                } catch (err) {
                    console.error('Error in handleBulkDelete:', err);
                    setError(t('common.error.unexpected'));
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleOpenDialog = (): void => {
        setOpenDialog(true);
    };

    const handleCloseDialog = (): void => {
        setOpenDialog(false);
        setAddEmployeeSubmitError(null);
        // Reset form
        setNewEmployee({
            firstName: '',
            lastName: '',
            personalNo: '',
            email: '',
            phone: '',
            hireDate: '',
            employmentType: 'Full Time',
            status: 'Active',
            isCitizen: true,
            taxExempt: false,
            residencyStatus: ''
            ,
            branch: '',
            bankAccount: emptyBankAccount()
        });
    };

    const handleCloseEditDialog = (): void => {
        setOpenEditDialog(false);
        setEditEmployeeSubmitError(null);
        // Reset edit form
        setEditingEmployee({
            firstName: '',
            lastName: '',
            personalNo: '',
            email: '',
            phone: '',
            hireDate: '',
            employmentType: 'Full Time',
            status: 'Active',
            taxId: '',
            isCitizen: true,
            taxExempt: false,
            residencyStatus: ''
            ,
            branch: ''
        });
        setEditingEmployeeId(null);
    };

    const handleEditFormChange = (field: keyof NewEmployee) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        setEditingEmployee({
            ...editingEmployee,
            [field]: event.target.value
        });
    };

    const handleEditCheckboxChange = (field: keyof NewEmployee) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        setEditingEmployee({
            ...editingEmployee,
            [field]: event.target.checked
        });
    };

    const handleFormChange = (field: keyof NewEmployee) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        setNewEmployee({
            ...newEmployee,
            [field]: event.target.value
        });
    };

    const handleCheckboxChange = (field: keyof NewEmployee) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        setNewEmployee({
            ...newEmployee,
            [field]: event.target.checked
        });
    };

    const handleSubmitEmployee = async (): Promise<void> => {
        console.log('handleSubmitEmployee called');

        if (!token) {
            console.error('No token found');
            setAddEmployeeSubmitError({message: t('employee.upload.error.noAuthToken'), violations: []});
            return;
        }


        console.log('Calling addEmployee with data:', newEmployee);
        setLoading(true);

        try {
            const result = await addEmployee(token, newEmployee);
            console.log('addEmployee result:', result);

            if (result.success) {
                // Refresh the employees list
                const employeesResult = await getEmployees(token);
                if (employeesResult.success && employeesResult.employees) {
                    setEmployees(employeesResult.employees);
                }
                handleCloseDialog();

                // Show success message
                setShowSuccessMessage(true);

                // Auto-close success message after 2 seconds
                setTimeout(() => {
                    setShowSuccessMessage(false);
                }, 2000);
            } else {
                setAddEmployeeSubmitError({
                    message: result.error || t('employee.error.addFailed'),
                    violations: result.violations || [],
                });
            }
        } catch (err) {
            console.error('Error in handleSubmitEmployee:', err);
            setAddEmployeeSubmitError({message: t('common.error.unexpected'), violations: []});
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateEmployee = async (): Promise<void> => {
        console.log('handleUpdateEmployee called');

        if (!token) {
            console.error('No token found');
            setEditEmployeeSubmitError({message: t('employee.upload.error.noAuthToken'), violations: []});
            return;
        }

        if (!editingEmployeeId) {
            console.error('No employee ID found');
            setEditEmployeeSubmitError({message: t('employee.error.noEmployeeSelectedForEditing'), violations: []});
            return;
        }

        // Validate required field
        if (!editingEmployee.residencyStatus) {
            console.error('Residency status not selected');
            setEditEmployeeSubmitError({message: t('employee.error.residencyStatusRequired'), violations: []});
            return;
        }

        console.log('Calling updateEmployee with data:', editingEmployeeId, editingEmployee);
        setLoading(true);

        try {
            const result = await updateEmployee(token, editingEmployeeId, editingEmployee);
            console.log('updateEmployee result:', result);

            if (result.success) {
                // Refresh the employees list
                const employeesResult = await getEmployees(token);
                if (employeesResult.success && employeesResult.employees) {
                    setEmployees(employeesResult.employees);
                }
                handleCloseEditDialog();

                // Show success message
                setSuccessMessageText(t('employee.success.updated'));
                setShowSuccessMessage(true);

                // Auto-close success message after 2 seconds
                setTimeout(() => {
                    setShowSuccessMessage(false);
                }, 2000);
            } else {
                setEditEmployeeSubmitError({
                    message: result.error || t('employee.error.updateFailed'),
                    violations: result.violations || [],
                });
            }
        } catch (err) {
            console.error('Error in handleUpdateEmployee:', err);
            setEditEmployeeSubmitError({message: t('common.error.unexpected'), violations: []});
        } finally {
            setLoading(false);
        }
    };

    const handleAddContract = (id: number): void => {
        console.log('Add contract for employee:', id);
        setSelectedEmployeeId(id);
        setOpenAddContractDialog(true);
    };

    const handleCloseAddContractDialog = (): void => {
        setOpenAddContractDialog(false);
        setSelectedEmployeeId(null);
        // Reset form
        setNewContract({
            contractNumber: '',
            contractId: '',
            startDate: '',
            endDate: '',
            status: 'active',
            jobPosition: '',
            payrollCalcType: '',
            withContribution: '',
            salaryBasis: '',
            baseAmount: '',
            workload: '',
            employerInsuranceBenefit: '',
            familyInsuranceDeduction: '',
            fundEnabled: '',
            notes: ''
        });
        setContractSubmitError(null);
    };

    const handleContractFormChange = (field: keyof Contract) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        setNewContract({
            ...newContract,
            [field]: event.target.value
        });
    };

    const handleSubmitContract = async (): Promise<void> => {
        console.log('handleSubmitContract called');
        console.log('Add new contract for employee:', selectedEmployeeId, newContract);

        if (!token) {
            console.error('No token found');
            setContractSubmitError({message: t('employee.upload.error.noAuthToken'), violations: []});
            return;
        }

        if (!selectedEmployeeId) {
            console.error('No employee selected');
            setContractSubmitError({message: t('employee.error.noEmployeeSelectedForContract'), violations: []});
            return;
        }

        setLoading(true);

        try {
            const result = await addContract(token, selectedEmployeeId.toString(), newContract);
            console.log('addContract result:', result);

            if (result.success) {
                console.log('Contract added successfully');
                handleCloseAddContractDialog();

                // Refresh the employees list to update the TableComponent
                const employeesResult = await getEmployees(token);
                if (employeesResult.success && employeesResult.employees) {
                    setEmployees(employeesResult.employees);
                }
            } else {
                setContractSubmitError({
                    message: result.error || t('employee.error.addContractFailed'),
                    violations: result.violations || [],
                });
            }
        } catch (err) {
            console.error('Error in handleSubmitContract:', err);
            setContractSubmitError({message: t('common.error.unexpected'), violations: []});
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    React.useEffect(() => {
        const fetchEmployees = async () => {
            if (!token) {
                setError(t('employee.upload.error.noAuthToken'));
                setLoading(false);
                return;
            }

            setLoading(true);
            let result;

            if (searchQuery.trim() === '') {
                result = await getEmployees(token);
            } else {
                result = await searchEmployees(token, searchQuery);
            }

            if (result.success && result.employees) {
                setEmployees(result.employees);
                setError(null);
                setCurrentPage(1);
            } else {
                setError(result.error || t('employee.error.fetchEmployeesFailed'));
            }
            setLoading(false);
        };

        fetchEmployees();
    }, [token, searchQuery, t]);

    const [downloading, setDownloading] = React.useState<boolean>(false);

    // ─── Upload employees state ───────────────────────────────────────────────
    const [uploading, setUploading] = React.useState<boolean>(false);
    const [uploadPreviewOpen, setUploadPreviewOpen] = React.useState<boolean>(false);
    const [uploadedEmployees, setUploadedEmployees] = React.useState<UploadedEmployee[]>([]);
    const [uploadError, setUploadError] = React.useState<UploadError | null>(null);
    const [templateDownloading, setTemplateDownloading] = React.useState<boolean>(false);

    const downloadEmployeeTemplate = async (): Promise<void> => {
        if (!token) return;
        setTemplateDownloading(true);
        try {
            const response = await fetch(            `${API_BASE_URL}/api/employees/template`, {
                method: 'GET',
                            headers: {Authorization: `Bearer ${token}`},
            });
            if (!response.ok) throw new Error('Template download failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'upload_employee_template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download employee template:', err);
            setUploadError({message: t('employee.upload.error.templateFailed'), errors: []});
        } finally {
            setTemplateDownloading(false);
        }
    };

    const handleUploadEmployees = async (file: File): Promise<void> => {
        if (!token) {
            console.error('No token found');
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API_BASE_URL}/api/employees/upload`, {
                method: 'POST',
                headers: {Authorization: `Bearer ${token}`},
                body: formData,
            });
            if (!response.ok) {
                let body: any = {};
                try { body = await response.json(); } catch { /* non-JSON error body */ }
                setUploadError({
                    message: body.message || t('employee.upload.error.generic'),
                    errors: Array.isArray(body.errors) ? body.errors : [],
                });
                return;
            }
            const data = await response.json();
            setUploadError(null);
            setUploadedEmployees((data.employees ?? []).map((emp: any, idx: number) => ({
                ...emp,
                _rowId: idx,
                status: true,
            })));
            setUploadPreviewOpen(true);
        } catch (err) {
            console.error('Failed to upload employees file:', err);
            setUploadError({message: t('employee.upload.error.generic'), errors: []});
        } finally {
            setUploading(false);
        }
    };

    const handleUploadedEmployeeChange = (rowId: number, field: keyof UploadedEmployee, value: any): void => {
        setUploadedEmployees((prev) =>
            prev.map((emp) => emp._rowId === rowId ? {...emp, [field]: value} : emp)
        );
    };

    const [uploadSaveLoading, setUploadSaveLoading] = React.useState<boolean>(false);
    const [uploadSaveError, setUploadSaveError] = React.useState<{ message: string; violations: string[] } | null>(null);

    const handleCloseUploadPreview = (): void => {
        setUploadPreviewOpen(false);
        setUploadedEmployees([]);
        setUploadSaveError(null);
    };

    const handleSaveUploadedEmployees = async (): Promise<void> => {
        if (!token) {
            setUploadSaveError({ message: t('employee.upload.error.noAuthToken'), violations: [] });
            return;
        }

        const payload = uploadedEmployees.map((emp) => ({
            branch: emp.branch,
            firstName: emp.firstName,
            lastName: emp.lastName,
            personalNo: emp.personalNo,
            email: emp.email,
            phone: emp.phone,
            employmentType: emp.employmentType,
            ...((emp.taxId || '').trim() ? {taxId: emp.taxId} : {}),
            ...(emp.bankAccount && (emp.bankAccount.bankId || emp.bankAccount.accountNumber)
                ? {bankAccount: {bankId: emp.bankAccount.bankId, accountNumber: emp.bankAccount.accountNumber}}
                : {}),
            status: emp.status ? 'Active' : 'Inactive',
            isCitizen: emp.isCitizen,
            isTaxEmpt: emp.isTaxEmpt,
            residencyStatus: emp.residencyStatus,
        }));

        setUploadSaveLoading(true);
        setUploadSaveError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/employees/add/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errBody: any = {};
                try { errBody = await response.json(); } catch { /* non-JSON error body */ }
                setUploadSaveError({
                    message: errBody.message || t('employee.upload.error.requestFailed', {status: response.status}),
                    violations: errBody.violations ?? [],
                });
                return;
            }

            // Success — refresh employee list, close dialog, show success
            const employeesResult = await getEmployees(token);
            if (employeesResult.success && employeesResult.employees) {
                setEmployees(employeesResult.employees);
            }
            handleCloseUploadPreview();
            setSuccessMessageText(t('employee.upload.employeesUploaded'));
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 2000);
        } catch (err) {
            console.error('Error saving uploaded employees:', err);
            setUploadSaveError({ message: t('common.error.unexpected'), violations: [] });
        } finally {
            setUploadSaveLoading(false);
        }
    };

    const downloadEmployees = async () => {
        setDownloading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/employees/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error('Download failed');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename = contentDisposition
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') ?? 'employees.xlsx'
                : 'employees.xlsx';
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download employees:', err);
        } finally {
            setDownloading(false);
        }
    };

    return {
        token,
        employees,
        loading,
        error,
        selected,
        searchInput,
        setSearchInput,
        setSearchQuery,
        branchFilter,
        setBranchFilter,
        statusFilter,
        setStatusFilter,
        branchOptions,
        statusOptions,
        facetFilteredEmployees,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        openDialog,
        openEditDialog,
        openViewDialog,
        openAddContractDialog,
        openConfirmDialog,
        confirmDialogMessage,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        selectedEmployeeContract,
        selectedEmployeeId,
        newEmployee,
        editingEmployee,
        newContract,
        contracts,
        contractsPage,
        contractsTotalRecords,
        showConfirmDialog,
        handleConfirmDialogClose,
        handleConfirmDialogNo,
        handleConfirmDialogYes,
        handleSelectAllClick,
        handleClick,
        isSelected,
        handleEdit,
        handleDelete,
        handleView,
        handleCloseViewDialog,
        handleActivate,
        handleDeactivate,
        handleBulkDelete,
        handleOpenDialog,
        handleCloseDialog,
        handleFormChange,
        handleCheckboxChange,
        handleSubmitEmployee,
        handleCloseEditDialog,
        handleEditFormChange,
        handleEditCheckboxChange,
        handleUpdateEmployee,
        handleAddContract,
        handleCloseAddContractDialog,
        handleContractFormChange,
        handleSubmitContract,
        // Contract pagination handlers
        handleContractsPageChange,
        handleConfirmTerminate,
        downloadEmployees,
        downloading,
        contractSubmitError,
        addEmployeeSubmitError,
        editEmployeeSubmitError,
        // Upload employees
        uploading,
        uploadPreviewOpen,
        uploadedEmployees,
        handleUploadEmployees,
        handleUploadedEmployeeChange,
        handleCloseUploadPreview,
        handleSaveUploadedEmployees,
        uploadSaveLoading,
        uploadSaveError,
        uploadError,
        clearUploadError: () => setUploadError(null),
        downloadEmployeeTemplate,
        templateDownloading,
        refreshEmployees: async () => {
            if (!token) return;
            const result = await getEmployees(token);
            if (result.success && result.employees) setEmployees(result.employees);
        },
        setSuccessMessageText,
    };
};

// ─── New employee form hook ───────────────────────────────────────────────────

export function useNewEmployeeForm(
    newEmployee: NewEmployee,
    showAllErrors: boolean = false,
    handleFormChange?: (field: keyof NewEmployee) => (event: any) => void
) {
    const {token} = useAuth();
    const [touched, setTouched] = React.useState<Partial<Record<keyof NewEmployee, boolean>>>({});
    const [approvedBranches, setApprovedBranches] = React.useState<ApprovedBranch[]>([]);
    const [branchesLoading, setBranchesLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!token) return;
        setBranchesLoading(true);
        fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((data) => setApprovedBranches(data.tenants ?? []))
            .catch(() => setApprovedBranches([]))
            .finally(() => setBranchesLoading(false));
    }, [token]);

    const handleBlur = (field: keyof NewEmployee) => () => {
        setTouched(prev => ({...prev, [field]: true}));
    };

    const isTouched = (field: keyof NewEmployee) => touched[field] || showAllErrors;

    const errors = validateEmployeeForm(newEmployee);

    // ── Bank account (add form only: present on `newEmployee.bankAccount`) ──
    const hasBankAccount = !!newEmployee.bankAccount;
    const [banks, setBanks] = React.useState<Bank[]>([]);

    React.useEffect(() => {
        if (!hasBankAccount || !token) return;
        let cancelled = false;
        getBanks(token).then((result) => {
            if (!cancelled && result.success && result.banks) setBanks(result.banks);
        });
        return () => {
            cancelled = true;
        };
    }, [hasBankAccount, token]);

    const [touchedBankAccount, setTouchedBankAccount] = React.useState<Partial<Record<BankAccountTextField, boolean>>>({});

    const bankAccount: BankAccount = newEmployee.bankAccount ?? emptyBankAccount();

    const handleBankAccountChange = (field: BankAccountTextField, value: string) => {
        handleFormChange?.('bankAccount')({target: {value: {...bankAccount, [field]: value}}});
    };

    const handleBankAccountBlur = (field: BankAccountTextField) => () => {
        setTouchedBankAccount((prev) => ({...prev, [field]: true}));
    };

    const isBankAccountTouched = (field: BankAccountTextField) => !!touchedBankAccount[field] || showAllErrors;

    return {
        touched,
        banks,
        bankAccount,
        handleBankAccountChange,
        handleBankAccountBlur,
        isBankAccountTouched,
        approvedBranches,
        branchesLoading,
        handleBlur,
        isTouched,
        errors,
    };
}

// ─── Table sort / filter / menu hook ─────────────────────────────────────────

export type EmployeeColumnKey = 'email' | 'phone' | 'employmentType' | 'status' | 'branch';

export const DEFAULT_EMPLOYEE_COLUMNS: Record<EmployeeColumnKey, boolean> = {
    email: true,
    phone: true,
    employmentType: true,
    status: true,
    branch: true,
};

export interface EmployeeColumnDef {
    key: EmployeeColumnKey;
    labelKey: string;
    field: SortableKey;
}

export const EMPLOYEE_COLUMN_DEFS: EmployeeColumnDef[] = [
    {key: 'email', labelKey: 'employee.form.field.email', field: 'email'},
    {key: 'phone', labelKey: 'employee.form.field.phone', field: 'phone'},
    {key: 'employmentType', labelKey: 'employee.form.field.employmentType', field: 'employmentType'},
    {key: 'status', labelKey: 'hr.branches.table.header.status', field: 'status'},
    {key: 'branch', labelKey: 'hr.jobs.table.header.branch', field: 'branch'},
];

export interface TableComponentProps {
    sx?: any;
    ariaLabel?: string;
    employees: Employee[];
    loading: boolean;
    error: string | null;
    selected: number[];
    page?: number;
    rowsPerPage?: number;
    searchQuery?: string;
    visibleColumns: Record<EmployeeColumnKey, boolean>;
    handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleClick: (event: React.MouseEvent<HTMLTableRowElement>, id: number) => void;
    isSelected: (id: number) => boolean;
    handleEdit: (id: number) => void;
    handleDelete: (id: number) => void;
    handleView: (id: number) => void;
    handleAddContract: (id: number) => void;
}

export type Order = 'asc' | 'desc';
export type SortableKey = 'firstName' | 'lastName' | 'email' | 'phone' | 'hireDate' | 'employmentType' | 'status' | 'branch';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? '';
    const bVal = b[orderBy] ?? '';
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function useTableSort({
    employees,
    searchQuery = '',
    page = 1,
    rowsPerPage = 20,
}: {
    employees: Employee[];
    searchQuery?: string;
    page?: number;
    rowsPerPage?: number;
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
    const open = Boolean(anchorEl);

    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<SortableKey>('firstName');

    const handleRequestSort = (property: SortableKey) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredEmployees = React.useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => {
            const phone = e.phone ? `(+383)${e.phone}` : '';
            return [
                e.firstName,
                e.lastName,
                e.email,
                phone,
                e.hireDate,
                e.employmentType,
                e.status,
                e.branch,
            ].some((val) => (val ?? '').toLowerCase().includes(q));
        });
    }, [employees, searchQuery]);

    const sortedEmployees = React.useMemo(
        () => [...filteredEmployees].sort(getComparator(order, orderBy)),
        [filteredEmployees, order, orderBy],
    );

    const paginatedEmployees = React.useMemo(
        () => sortedEmployees.slice((page - 1) * rowsPerPage, page * rowsPerPage),
        [sortedEmployees, page, rowsPerPage],
    );

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    return {
        anchorEl,
        selectedRowId,
        open,
        order,
        orderBy,
        handleRequestSort,
        filteredEmployees,
        paginatedEmployees,
        handleMenuClick,
        handleMenuClose,
        handleMenuAction,
    };
}

// ─── Mobile table hook ────────────────────────────────────────────────────────

export interface TableComponentMobileProps {
    employees: Employee[];
    loading: boolean;
    error: string | null;
    selected: number[];
    page?: number;
    rowsPerPage?: number;
    searchQuery?: string;
    handleClick: (event: React.MouseEvent<HTMLTableRowElement>, id: number) => void;
    isSelected: (id: number) => boolean;
    handleEdit: (id: number) => void;
    handleView: (id: number) => void;
    handleAddContract: (id: number) => void;
}

export function useTableMobile({
    employees,
    page = 1,
    rowsPerPage = 10,
}: {
    employees: Employee[];
    page?: number;
    rowsPerPage?: number;
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
    const open = Boolean(anchorEl);

    const paginatedEmployees = React.useMemo(() => {
        const startIndex = (page - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return employees.slice(startIndex, endIndex);
    }, [employees, page, rowsPerPage]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    return {
        anchorEl,
        selectedRowId,
        open,
        paginatedEmployees,
        handleMenuClick,
        handleMenuClose,
        handleMenuAction,
    };
}
